import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { Alert, Button, Empty, Modal, Skeleton, Space, Tooltip, Typography } from 'antd'
import {
  ArrowLeftOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { useToast } from '@/contexts/ToastContext'
import { useAuthSession } from '@/lib/session'
import { permissions } from '@/types/authorization'
import type { McpPlaybookSummary } from '@/types/mcp'
import { isForbidden, useCanManageMcp, useDeleteMcpPlaybookMutation, useMcpPlaybooks } from '../hooks'
import PlaybookDrawer from './PlaybookDrawer'
import PlaybookForm, { type PlaybookFormMode } from './PlaybookForm'
import { InputsSummary } from './PlaybookInputs'
import PlaybookTags from './PlaybookTags'

const GROUPS: { title: string; empty: (isAdmin: boolean) => string; includes: (playbook: McpPlaybookSummary) => boolean }[] = [
  {
    title: 'Starters',
    empty: () => 'No starters for this client.',
    includes: (playbook) => playbook.scope === 'starter' || playbook.scope === 'firmEdit',
  },
  {
    title: 'Your firm’s',
    empty: (isAdmin) =>
      isAdmin
        ? 'Your firm hasn’t saved any playbooks yet. Do the work in your AI assistant, then ask it to save it as a playbook for the firm.'
        : 'Your firm hasn’t saved any playbooks yet. Your firm’s admins can save playbooks for everyone.',
    includes: (p) => p.scope === 'firm',
  },
  {
    title: 'Yours',
    empty: () =>
      'You haven’t saved any playbooks yet. Do the work in your AI assistant, then ask it to save it as a playbook. Only you will see it.',
    includes: (p) => p.scope === 'personal',
  },
]

export default function PlaybooksPage() {
  const navigate = useNavigate()
  const session = useAuthSession()
  const { hasPermission } = useAuthorization()
  const { addToast } = useToast()
  const clientId = session?.currentClientId ?? ''
  const clientName = session?.currentClientName || 'this client'
  const canCreate = hasPermission(permissions.updateAgentBuilder)
  const manage = useCanManageMcp(clientId)
  const isAdmin = manage.canManage
  const playbooks = useMcpPlaybooks(clientId)
  const remove = useDeleteMcpPlaybookMutation(clientId)
  const [viewing, setViewing] = useState<string | null>(null)
  const [formMode, setFormMode] = useState<PlaybookFormMode | null>(null)

  const confirmDelete = (playbook: McpPlaybookSummary) => {
    const resetting = playbook.scope === 'firmEdit'
    Modal.confirm({
      title: resetting ? `Go back to the starter version of ${playbook.name}?` : `Delete ${playbook.name}?`,
      content: resetting
        ? 'Your firm’s changes are deleted for everyone.'
        : playbook.scope === 'firm'
          ? 'AI assistants won’t be able to run it any more, for anyone at the firm.'
          : 'Your AI assistant won’t be able to run it any more.',
      okText: resetting ? 'Go back to the starter' : 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await remove.mutateAsync(playbook.id)
          addToast(resetting ? `${playbook.name} is back to the starter` : `Deleted ${playbook.name}`, 'success')
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Couldn’t delete this playbook.', 'error')
        }
      },
    })
  }

  const renderRow = (playbook: McpPlaybookSummary) => {
    const canEdit = canCreate && playbook.canEdit
    const canDelete = canEdit && (playbook.scope === 'personal' || playbook.scope === 'firm')
    const canReset = playbook.scope === 'firmEdit' && canEdit
    return (
      <li
        key={playbook.id}
        className="flex flex-wrap items-start gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-card"
      >
        <div className="min-w-0 grow basis-80">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="border-0 bg-transparent p-0 text-left font-display text-base font-semibold text-gray-900 hover:text-brand-700 cursor-pointer"
              onClick={() => setViewing(playbook.id)}
            >
              {playbook.name}
            </button>
            {playbook.when && <span className="text-sm text-gray-500">{playbook.when}</span>}
            <PlaybookTags playbook={playbook} />
          </div>
          {playbook.description && <p className="mt-1 mb-0 text-sm text-gray-700">{playbook.description}</p>}
          <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-gray-500">
            <InputsSummary inputs={playbook.inputs} />
            {playbook.editedBy && (
              <span>
                Changed by {playbook.editedBy}
                {playbook.updatedAt ? `, ${dayjs(playbook.updatedAt).format('DD/MM/YYYY')}` : ''}
              </span>
            )}
          </div>
        </div>
        <Space size={4} className="shrink-0">
          <Tooltip title="View">
            <Button type="text" icon={<EyeOutlined />} aria-label={`View ${playbook.name}`} onClick={() => setViewing(playbook.id)} />
          </Tooltip>
          {canEdit && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label={`Edit ${playbook.name}`}
                onClick={() => setFormMode({ kind: 'edit', id: playbook.id })}
              />
            </Tooltip>
          )}
          {canCreate && !playbook.adminOnly && (
            <Tooltip title="Make a copy">
              <Button
                type="text"
                icon={<CopyOutlined />}
                aria-label={`Make a copy of ${playbook.name}`}
                onClick={() => setFormMode({ kind: 'copy', id: playbook.id })}
              />
            </Tooltip>
          )}
          {canReset && (
            <Tooltip title="Go back to the starter">
              <Button
                type="text"
                icon={<UndoOutlined />}
                aria-label={`Go back to the starter version of ${playbook.name}`}
                onClick={() => confirmDelete(playbook)}
              />
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label={`Delete ${playbook.name}`}
                onClick={() => confirmDelete(playbook)}
              />
            </Tooltip>
          )}
        </Space>
      </li>
    )
  }

  const renderBody = () => {
    if (!clientId) return <Alert type="info" showIcon message="Choose a client to see its playbooks." />
    if (playbooks.isLoading) return <Skeleton active paragraph={{ rows: 8 }} />
    if (playbooks.isError) {
      if (isForbidden(playbooks.error)) {
        return (
          <Alert
            type="info"
            showIcon
            message={`Playbooks are for AI assistants. Ask your admin for MCP access to ${clientName}.`}
          />
        )
      }
      return (
        <Alert
          type="error"
          showIcon
          message={playbooks.error instanceof Error ? playbooks.error.message : 'Couldn’t load the playbooks.'}
          action={<Button onClick={() => void playbooks.refetch()}>Try again</Button>}
        />
      )
    }
    const all = playbooks.data ?? []
    return (
      <Space orientation="vertical" size={28} className="w-full">
        {GROUPS.map((group) => {
          const rows = all.filter(group.includes)
          return (
            <section key={group.title}>
              <h2 className="font-display text-lg font-semibold text-gray-900 mb-3">{group.title}</h2>
              {rows.length > 0 ? (
                <ul className="m-0 list-none space-y-2 p-0">{rows.map(renderRow)}</ul>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={group.empty(isAdmin)} className="!my-2" />
              )}
            </section>
          )
        })}
      </Space>
    )
  }

  const forbidden = playbooks.isError && isForbidden(playbooks.error)

  return (
    <FeatureThemeProvider featureKey="pracIntel" withAntd className="px-6 py-6 md:px-8">
      <div className="relative mx-auto w-full max-w-6xl">
        <div className="pt-1 pb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <Typography.Title level={3} className="!mb-1">
                Playbooks
              </Typography.Title>
              <Typography.Text type="secondary">
                The jobs your AI assistant runs when you ask for one by name, for {clientName}. Nothing runs on this page.
              </Typography.Text>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Space wrap>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports/dashboard-builder')}>
                  Back to PracIntel
                </Button>
                {canCreate && clientId && !forbidden && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormMode({ kind: 'new' })}>
                    New playbook
                  </Button>
                )}
              </Space>
              {canCreate && clientId && !forbidden && (
                <Typography.Text type="secondary" className="max-w-xs text-right text-xs">
                  Tip: do the work once in your AI assistant, then ask it to save it as a playbook.
                </Typography.Text>
              )}
            </div>
          </div>
        </div>

        {manage.status === 'error' && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="Couldn’t check whether you’re an admin here, so saving for the whole firm is hidden."
            action={<Button size="small" onClick={manage.retry}>Try again</Button>}
          />
        )}
        {renderBody()}
      </div>

      <PlaybookDrawer
        clientId={clientId}
        playbookId={viewing}
        onClose={() => setViewing(null)}
        onEdit={
          canCreate
            ? (id) => {
                setViewing(null)
                setFormMode({ kind: 'edit', id })
              }
            : undefined
        }
      />
      <PlaybookForm clientId={clientId} mode={formMode} isAdmin={isAdmin} onClose={() => setFormMode(null)} />
    </FeatureThemeProvider>
  )
}
